/**
 * Repositório Firestore: coleção `leads` + subcoleção `timeline`.
 * Não usa data/main — alinhado à separação do monólito.
 */

import {
  LEAD_STATUS,
  LEAD_STATUS_LABEL,
  LEAD_STATUS_ORDER,
} from './leadConstants.js';
import { resolveOrigemLeadForFirestore } from './origemLeadNormalize.js';
import { assertCrmAccess } from './crmAccess.js';
import { clientExistsInDataMain } from './crmDataMainClient.js';
import { logSystemActivity, getActivityActorName } from '../lib/systemActivityLog.js';

const LEADS = 'leads';
const ONBOARDING = 'onboarding';

function db() {
  assertCrmAccess();
  return window._fbDb;
}

function nowMs() {
  return Date.now();
}

function actorFromWindow() {
  const u = window._currentUser;
  return {
    uid: u?.uid || '',
    name: (u?.displayName || u?.email || '').trim() || 'Admin',
  };
}

/** @param {unknown} ts */
export function tsToMs(ts) {
  if (ts == null) return null;
  if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (ts instanceof Date) return ts.getTime();
  return null;
}

/** @param {unknown} s */
export function onlyDigits(s) {
  return String(s ?? '').replace(/\D/g, '');
}

/**
 * Consentimentos e flags booleanas opcionais: true / false / null (sem registo).
 * @param {unknown} v
 * @returns {true|null|false}
 */
export function normalizeTrilean(v) {
  if (v === true || v === 'true' || v === 1 || v === '1') return true;
  if (v === false || v === 'false' || v === 0 || v === '0') return false;
  return null;
}

/**
 * Serviço "pacote completo": canónico `Completo`; legado `Todos` (e variações de capitalização de `Completo`).
 * Só token inteiro após trim — não altera frases que contenham essas palavras.
 * @param {string} trimmed
 * @returns {string}
 */
function canonicalLeadServicoToken(trimmed) {
  const lower = trimmed.toLowerCase();
  if (lower === 'todos' || lower === 'completo') return 'Completo';
  return trimmed;
}

/**
 * Junta `servicos` e alias `servicosInteresse` (site / legado), dedupe case-insensitive, ordem estável.
 * `Todos` / `Completo` (exact match, any case) → `Completo`.
 * @param {unknown} servicos
 * @param {unknown} servicosInteresse
 * @returns {string[]}
 */
export function mergeLeadServicosArrays(servicos, servicosInteresse) {
  const a = Array.isArray(servicos) ? servicos.map(String) : [];
  const b = Array.isArray(servicosInteresse) ? servicosInteresse.map(String) : [];
  const seen = new Set();
  const out = [];
  for (const x of [...a, ...b]) {
    const t = x.trim();
    if (!t) continue;
    const canon = canonicalLeadServicoToken(t);
    const k = canon.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(canon);
  }
  return out;
}

/**
 * @param {Record<string, unknown>} raw
 * @param {string} id
 */
export function normalizeLeadDoc(raw, id) {
  const servicos = mergeLeadServicosArrays(raw.servicos, raw.servicosInteresse);
  const waLegacy = String(raw.whatsapp ?? '');
  const waMasked = String(raw.whatsappMasked ?? '');
  const waDigits = onlyDigits(raw.whatsappDigits) || onlyDigits(waLegacy);
  const whatsappDisplay = waMasked.trim() || waLegacy.trim();
  const receivedAt = tsToMs(raw.receivedAt);
  return {
    id,
    nome: String(raw.nome ?? ''),
    email: String(raw.email ?? ''),
    whatsapp: whatsappDisplay,
    whatsappMasked: waMasked.trim(),
    whatsappDigits: waDigits,
    cidade: String(raw.cidade ?? ''),
    estado: String(raw.estado ?? ''),
    especialidade: String(raw.especialidade ?? ''),
    instagram: String(raw.instagram ?? ''),
    origem: String(raw.origem ?? ''),
    servicos,
    mensagem: raw.mensagem == null || raw.mensagem === '' ? '' : String(raw.mensagem),
    data_envio: tsToMs(raw.data_envio) ?? receivedAt ?? tsToMs(raw.createdAt) ?? null,
    receivedAt,
    origem_pagina: String(raw.origem_pagina ?? ''),
    status: LEAD_STATUS_ORDER.includes(/** @type {string} */ (raw.status)) ? raw.status : LEAD_STATUS.NOVO,
    responsavelUid: String(raw.responsavelUid ?? ''),
    responsavelName: String(raw.responsavelName ?? ''),
    valorEstimado: Number.isFinite(Number(raw.valorEstimado)) && Number(raw.valorEstimado) > 0 ? Number(raw.valorEstimado) : 0,
    lostReason: String(raw.lostReason ?? ''),
    lastContactAt: tsToMs(raw.lastContactAt),
    createdAt: tsToMs(raw.createdAt),
    updatedAt: tsToMs(raw.updatedAt),
    archived: raw.archived === true,
    archivedAt: tsToMs(raw.archivedAt),
    convertedAt: tsToMs(raw.convertedAt),
    convertedClientId: String(raw.convertedClientId ?? ''),
    entrada: String(raw.entrada ?? 'manual'),
    nextAction: String(raw.nextAction ?? ''),
    followUpAt: tsToMs(raw.followUpAt),
    formType: String(raw.formType ?? '').trim(),
    privacyConsent: normalizeTrilean(raw.privacyConsent),
    privacyConsentAt: tsToMs(raw.privacyConsentAt),
    privacyConsentSource: String(raw.privacyConsentSource ?? '').trim(),
    marketingConsent: normalizeTrilean(raw.marketingConsent),
    marketingConsentAt: tsToMs(raw.marketingConsentAt),
    marketingConsentSource: String(raw.marketingConsentSource ?? '').trim(),
    userAgent: String(raw.userAgent ?? '').trim(),
    duplicate: raw.duplicate === true || raw.duplicate === 'true',
    source: String(raw.source ?? raw.canal ?? '').trim(),
    channel: String(raw.channel ?? '').trim(),
    origemLead: resolveOrigemLeadForFirestore(raw.origemLead, raw.source ?? raw.canal),
    origemLeadOutro: String(raw.origemLeadOutro ?? '').trim(),
    indicacaoColega: String(raw.indicacaoColega ?? '').trim(),
    onboardingId: String(raw.onboardingId ?? '').trim(),
    sentToOnboardingAt: tsToMs(raw.sentToOnboardingAt),
    createdVia: String(raw.createdVia ?? '').trim(),
    pendingClientSelfFill: raw.pendingClientSelfFill === true,
  };
}

/**
 * Subscrição à listagem (ordenado por createdAt desc).
 * @param {(leads: ReturnType<typeof normalizeLeadDoc>[]) => void} onData
 * @param {(e: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeLeads(onData, onError) {
  const database = db();
  if (!database || !window._fbCollection || !window._fbQuery || !window._fbOrderBy || !window._fbOnSnapshot) {
    onData([]);
    return () => {};
  }
  const col = window._fbCollection(database, LEADS);
  const q = window._fbQuery(col, window._fbOrderBy('createdAt', 'desc'));
  return window._fbOnSnapshot(
    q,
    snap => {
      const list = snap.docs.map(d => normalizeLeadDoc(d.data(), d.id));
      onData(list);
    },
    err => {
      console.warn('[CRM] subscribeLeads', err);
      if (typeof onError === 'function') onError(err);
      onData([]);
    },
  );
}

/**
 * @param {string} leadId
 * @param {(events: object[]) => void} onData
 */
export function subscribeTimeline(leadId, onData) {
  const database = db();
  if (!leadId || !database || !window._fbCollection || !window._fbQuery || !window._fbOrderBy || !window._fbOnSnapshot) {
    onData([]);
    return () => {};
  }
  const col = window._fbCollection(database, LEADS, leadId, 'timeline');
  const q = window._fbQuery(col, window._fbOrderBy('at', 'desc'));
  return window._fbOnSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    },
    () => onData([]),
  );
}

/**
 * @param {object} p
 * @param {{ notify?: boolean }} [opts]
 */
/**
 * @param {Record<string, unknown>} p
 * @param {number} t
 */
function buildLeadFirestorePayload(p, t) {
  const email = String(p.email || '').trim().toLowerCase();
  const status =
    p.status && LEAD_STATUS_ORDER.includes(/** @type {string} */ (p.status)) ? p.status : LEAD_STATUS.NOVO;
  const entrada = String(p.entrada || 'manual').trim() || 'manual';
  const dataEnvio =
    typeof p.data_envio === 'number' && Number.isFinite(p.data_envio) ? p.data_envio : t;
  const origemPage = String(p.origem_pagina || '').trim();
  const whatsappPlain = String(p.whatsapp || '').trim();
  const waDigits = onlyDigits(p.whatsappDigits) || onlyDigits(whatsappPlain);
  const ft = String(p.formType || '').trim();
  const recAt = tsToMs(p.receivedAt);
  const servicosMerged = mergeLeadServicosArrays(p.servicos, p.servicosInteresse);
  return {
    nome: String(p.nome || '').trim(),
    email,
    whatsapp: whatsappPlain,
    whatsappMasked: String(p.whatsappMasked || '').trim(),
    whatsappDigits: waDigits,
    cidade: String(p.cidade || '').trim(),
    estado: String(p.estado || '').trim(),
    especialidade: String(p.especialidade || '').trim(),
    instagram: String(p.instagram || '').trim(),
    origem: String(p.origem || (entrada === 'import' ? 'import_csv' : 'manual')).trim() || 'manual',
    servicos: servicosMerged,
    mensagem: String(p.mensagem || '').trim(),
    data_envio: dataEnvio,
    receivedAt: recAt,
    origem_pagina: origemPage || (entrada === 'import' ? 'import_csv' : 'dashboard'),
    status,
    responsavelUid: String(p.responsavelUid || '').trim(),
    responsavelName: String(p.responsavelName || '').trim(),
    createdAt: t,
    updatedAt: t,
    archived: false,
    archivedAt: null,
    convertedAt: null,
    convertedClientId: null,
    entrada,
    nextAction: String(p.nextAction || '').trim(),
    followUpAt: typeof p.followUpAt === 'number' && Number.isFinite(p.followUpAt) ? p.followUpAt : null,
    valorEstimado: Number.isFinite(Number(p.valorEstimado)) && Number(p.valorEstimado) > 0 ? Number(p.valorEstimado) : null,
    lostReason: String(p.lostReason || '').trim() || null,
    formType: ft || null,
    privacyConsent: normalizeTrilean(p.privacyConsent),
    privacyConsentAt: tsToMs(p.privacyConsentAt),
    privacyConsentSource: String(p.privacyConsentSource || '').trim() || null,
    marketingConsent: normalizeTrilean(p.marketingConsent),
    marketingConsentAt: tsToMs(p.marketingConsentAt),
    marketingConsentSource: String(p.marketingConsentSource || '').trim() || null,
    userAgent: String(p.userAgent || '').trim() || null,
    duplicate: p.duplicate === true ? true : null,
    source: String(p.source || p.canal || '').trim() || null,
    channel: String(p.channel || '').trim() || null,
    origemLead: resolveOrigemLeadForFirestore(p.origemLead, p.source || p.canal),
    origemLeadOutro: String(p.origemLeadOutro || '').trim() || null,
    indicacaoColega: String(p.indicacaoColega || '').trim() || null,
    onboardingId: String(p.onboardingId || '').trim() || null,
    sentToOnboardingAt: tsToMs(p.sentToOnboardingAt),
    ...(String(p.createdVia || '').trim()
      ? { createdVia: String(p.createdVia).trim().slice(0, 48) }
      : {}),
    ...(p.pendingClientSelfFill === true ? { pendingClientSelfFill: true } : {}),
  };
}

export async function createLeadManual(p, opts = {}) {
  const database = db();
  if (!database) throw new Error('Firebase não disponível');
  const email = String(p.email || '').trim().toLowerCase();
  const nome = String(p.nome || '').trim();
  const wa = String(p.whatsapp || '').trim();
  if (!email && !nome && !wa) {
    throw new Error('Indique pelo menos nome, e-mail ou WhatsApp');
  }

  const t = nowMs();
  const payload = buildLeadFirestorePayload(p, t);

  const col = window._fbCollection(database, LEADS);
  const ref = await window._fbAddDoc(col, payload);
  const evCol = window._fbCollection(database, LEADS, ref.id, 'timeline');
  const { uid, name } = actorFromWindow();
  const timelineMsg =
    opts.timelineMessage ||
    (payload.entrada === 'import' ? 'Lead criado (importação CSV)' : 'Lead criado');
  const meta = opts.sourceMeta != null ? opts.sourceMeta : { source: payload.entrada === 'import' ? 'import_csv' : 'manual' };
  await window._fbAddDoc(evCol, {
    kind: 'created',
    at: window._fbServerTimestamp(),
    actorUid: uid,
    actorName: name,
    message: timelineMsg,
    meta,
  });

  if (opts.notify !== false && typeof window.notifyAdminsCrmNewLead === 'function') {
    await window.notifyAdminsCrmNewLead({
      leadId: ref.id,
      nome: payload.nome,
      email: payload.email,
      excludeSelf: true,
    });
  }
  return ref.id;
}

/**
 * Importação em massa (batch). Não envia notificação por lead.
 * @param {{ toCreate: { line: number, payload: Record<string, unknown> }[], toUpdate: { line: number, leadId: string, payload: Record<string, unknown> }[] }} blocks
 * @returns {Promise<{ created: number, updated: number }>}
 */
export async function commitBulkLeadImport(blocks) {
  const database = db();
  if (!database) throw new Error('Firebase não disponível');
  const { toCreate = [], toUpdate = [] } = blocks || {};
  const col = window._fbCollection(database, LEADS);
  const { uid, name } = actorFromWindow();

  const MAX_OPS = 400;
  let created = 0;
  let updated = 0;

  for (let i = 0; i < toCreate.length; ) {
    const batch = window._fbWriteBatch();
    let ops = 0;
    while (i < toCreate.length && ops < MAX_OPS) {
      const row = toCreate[i];
      const t = nowMs();
      const payload = buildLeadFirestorePayload(row.payload, t);
      const ref = window._fbDoc(col);
      batch.set(ref, payload);
      const tlCol = window._fbCollection(database, LEADS, ref.id, 'timeline');
      const tlRef = window._fbDoc(tlCol);
      batch.set(tlRef, {
        kind: 'created',
        at: window._fbServerTimestamp(),
        actorUid: uid,
        actorName: name,
        message: 'Lead criado (importação em massa)',
        meta: { source: 'import_csv', line: row.line },
      });
      ops += 2;
      created++;
      i++;
    }
    await batch.commit();
  }

  for (let i = 0; i < toUpdate.length; ) {
    const batch = window._fbWriteBatch();
    let ops = 0;
    while (i < toUpdate.length && ops < MAX_OPS) {
      const row = toUpdate[i];
      const leadRef = window._fbDoc(database, LEADS, row.leadId);
      const p = row.payload;
      const waImp = String(p.whatsapp || '').trim();
      const patch = {
        nome: String(p.nome || '').trim(),
        whatsapp: waImp,
        whatsappMasked: String(p.whatsappMasked || '').trim(),
        whatsappDigits: onlyDigits(p.whatsappDigits) || onlyDigits(waImp),
        cidade: String(p.cidade || '').trim(),
        estado: String(p.estado || '').trim(),
        especialidade: String(p.especialidade || '').trim(),
        instagram: String(p.instagram || '').trim(),
        origem: String(p.origem || '').trim() || 'import_csv',
        servicos: mergeLeadServicosArrays(p.servicos, []),
        mensagem: String(p.mensagem || '').trim(),
        origem_pagina: String(p.origem_pagina || '').trim() || 'import_csv',
        source: String(p.source || p.canal || '').trim() || null,
        channel: String(p.channel || '').trim() || null,
        origemLead: resolveOrigemLeadForFirestore(p.origemLead, p.source || p.canal),
        status:
          p.status && LEAD_STATUS_ORDER.includes(/** @type {string} */ (p.status))
            ? p.status
            : LEAD_STATUS.NOVO,
        responsavelUid: String(p.responsavelUid || '').trim(),
        responsavelName: String(p.responsavelName || '').trim(),
        followUpAt: typeof p.followUpAt === 'number' && Number.isFinite(p.followUpAt) ? p.followUpAt : null,
        updatedAt: nowMs(),
      };
      batch.update(leadRef, patch);
      const tlCol = window._fbCollection(database, LEADS, row.leadId, 'timeline');
      const tlRef = window._fbDoc(tlCol);
      batch.set(tlRef, {
        kind: 'edit',
        at: window._fbServerTimestamp(),
        actorUid: uid,
        actorName: name,
        message: 'Dados atualizados por importação em massa',
        meta: { source: 'import_csv', line: row.line },
      });
      ops += 2;
      updated++;
      i++;
    }
    await batch.commit();
  }

  if (created > 0 || updated > 0) {
    const actor = getActivityActorName();
    void logSystemActivity({
      category: 'admin',
      verb: 'import',
      actionKey: 'admin.crm_leads_bulk_csv',
      summary: `${actor} importou leads em massa (CSV): ${created} criado(s), ${updated} atualizado(s)`,
      entityType: 'crm_leads',
      entityId: '',
      entityLabel: `+${created} / ~${updated}`,
      changeTags: [`leads_created:${created}`, `leads_updated:${updated}`],
    });
  }

  return { created, updated };
}

/**
 * @param {string} leadId
 * @param {Record<string, unknown>} patch
 * @param {string} timelineMessage
 * @param {object} [meta]
 * @param {'edit'|'status'|'note'|'assign'|'archive'} [kind]
 */
export async function patchLead(leadId, patch, timelineMessage, meta = {}, kind = 'edit') {
  const database = db();
  if (!leadId || !database) throw new Error('Dados inválidos');
  const { uid, name } = actorFromWindow();
  const ref = window._fbDoc(database, LEADS, leadId);
  const payload = { ...patch, updatedAt: nowMs() };
  if (Object.prototype.hasOwnProperty.call(patch, 'servicos')) {
    payload.servicos = mergeLeadServicosArrays(patch.servicos, []);
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'origemLead')) {
    payload.origemLead = resolveOrigemLeadForFirestore(
      patch.origemLead,
      Object.prototype.hasOwnProperty.call(patch, 'source')
        ? patch.source
        : Object.prototype.hasOwnProperty.call(patch, 'canal')
          ? patch.canal
          : undefined,
    );
  }
  await window._fbUpdateDoc(ref, payload);
  const evCol = window._fbCollection(database, LEADS, leadId, 'timeline');
  await window._fbAddDoc(evCol, {
    kind,
    at: window._fbServerTimestamp(),
    actorUid: uid,
    actorName: name,
    message: timelineMessage,
    meta: meta || {},
  });
}

/**
 * @param {string} leadId
 * @param {string} status
 */
export async function setLeadStatus(leadId, status) {
  if (!LEAD_STATUS_ORDER.includes(status)) throw new Error('Status inválido');
  const label = LEAD_STATUS_LABEL[status] || status;
  await patchLead(leadId, { status }, `Status alterado para ${label}`, { toStatus: status }, 'status');
}

/**
 * @param {string} leadId
 * @param {string} text
 */
export async function addLeadNote(leadId, text) {
  const t = String(text || '').trim();
  if (!t) throw new Error('Observação vazia');
  await patchLead(leadId, {}, 'Observação adicionada', { note: t }, 'note');
}

/**
 * @param {string} leadId
 * @param {string} responsavelUid
 * @param {string} responsavelName
 */
export async function assignResponsible(leadId, responsavelUid, responsavelName) {
  const uid = String(responsavelUid || '').trim();
  const rname = String(responsavelName || '').trim();
  await patchLead(
    leadId,
    { responsavelUid: uid, responsavelName: rname },
    uid ? `Responsável: ${rname || uid}` : 'Responsável removido',
    { responsavelUid: uid },
    'assign',
  );
}

/**
 * @param {string} leadId
 */
export async function archiveLead(leadId) {
  const t = nowMs();
  await patchLead(leadId, { archived: true, archivedAt: t }, 'Lead arquivado', {}, 'archive');
}

/**
 * @param {string} leadId
 */
export async function unarchiveLead(leadId) {
  await patchLead(leadId, { archived: false, archivedAt: null }, 'Lead desarquivado', {}, 'archive');
}

/**
 * Motivo para não mostrar / desativar "Excluir permanentemente" (lead arquivado).
 * @param {ReturnType<typeof normalizeLeadDoc>|null|undefined} lead
 * @returns {string} vazio = pode excluir (UI: só para leads arquivados)
 */
export function getLeadPermanentDeleteBlockReason(lead) {
  if (!lead) return '';
  if (!lead.archived) return '';
  if (String(lead.convertedClientId || '').trim()) {
    return 'Este lead foi convertido em cliente. O registo é mantido por auditoria; a exclusão não está disponível.';
  }
  if (String(lead.onboardingId || '').trim()) {
    return 'Há onboarding vinculado a este lead. Trate ou arquive o onboarding e remova o vínculo antes de excluir o lead.';
  }
  return '';
}

/**
 * Remove o documento `leads/{id}` e todos os eventos em `leads/{id}/timeline/*`.
 * Só para leads **já arquivados**, sem cliente convertido e sem `onboardingId`.
 *
 * Requer regra Firestore que permita `delete` em `leads/{id}/timeline/{eventId}` para admin
 * (hoje só create está permitido; ver checklist nas notas de deploy do CRM).
 *
 * @param {string} leadId
 */
export async function deleteArchivedLeadPermanent(leadId) {
  const database = db();
  if (!leadId || !database) throw new Error('Dados inválidos');
  if (!window._fbGetDoc || !window._fbDeleteDoc || !window._fbGetDocs || !window._fbWriteBatch) {
    throw new Error('Firebase incompleto');
  }
  const ref = window._fbDoc(database, LEADS, leadId);
  const snap = await window._fbGetDoc(ref);
  if (!snap.exists()) throw new Error('Lead não encontrado');
  const lead = normalizeLeadDoc(snap.data(), leadId);
  if (!lead.archived) throw new Error('Só é possível excluir leads arquivados');
  if (String(lead.convertedClientId || '').trim()) {
    throw new Error('Não é possível excluir: lead convertido em cliente.');
  }
  if (String(lead.onboardingId || '').trim()) {
    throw new Error('Não é possível excluir: há onboarding vinculado a este lead.');
  }

  const tlCol = window._fbCollection(database, LEADS, leadId, 'timeline');
  const tlSnap = await window._fbGetDocs(tlCol);
  const chunkSize = 400;
  for (let i = 0; i < tlSnap.docs.length; i += chunkSize) {
    const batch = window._fbWriteBatch();
    const slice = tlSnap.docs.slice(i, i + chunkSize);
    for (const d of slice) batch.delete(d.ref);
    await batch.commit();
  }
  await window._fbDeleteDoc(ref);
}

/**
 * Exclui lead diretamente (sem exigir arquivar), limpando timeline.
 * Se houver onboarding vinculado e não convertido, pode excluir junto (cascade).
 * Também remove convites do lead para evitar tokens órfãos.
 *
 * @param {string} leadId
 * @param {{ cascadeOnboarding?: boolean }} [opts]
 */
export async function deleteLeadPermanent(leadId, opts = {}) {
  const database = db();
  if (!leadId || !database) throw new Error('Dados inválidos');
  if (!window._fbGetDoc || !window._fbDeleteDoc || !window._fbGetDocs || !window._fbWriteBatch) {
    throw new Error('Firebase incompleto');
  }
  const cascadeOnboarding = opts.cascadeOnboarding !== false;

  const leadRef = window._fbDoc(database, LEADS, leadId);
  const snap = await window._fbGetDoc(leadRef);
  if (!snap.exists()) throw new Error('Lead não encontrado');
  const lead = normalizeLeadDoc(snap.data(), leadId);
  const leadCid = String(lead.convertedClientId || '').trim();
  if (leadCid && (await clientExistsInDataMain(leadCid))) {
    throw new Error('Este lead está vinculado a um cliente ativo e não pode ser excluído.');
  }

  const obId = String(lead.onboardingId || '').trim();
  if (obId) {
    const obRef = window._fbDoc(database, ONBOARDING, obId);
    const obSnap = await window._fbGetDoc(obRef);
    if (obSnap.exists()) {
      const obRaw = obSnap.data() || {};
      const obCid = String(obRaw.convertedClientId || '').trim();
      if (obCid && (await clientExistsInDataMain(obCid))) {
        throw new Error(
          'Não é possível excluir: o onboarding vinculado está associado a um cliente ativo.',
        );
      }
      if (!cascadeOnboarding) {
        throw new Error('Lead possui onboarding vinculado. Ative exclusão em cascata.');
      }
      const obTlCol = window._fbCollection(database, ONBOARDING, obId, 'timeline');
      const obTlSnap = await window._fbGetDocs(obTlCol);
      for (let i = 0; i < obTlSnap.docs.length; i += 400) {
        const b = window._fbWriteBatch();
        for (const d of obTlSnap.docs.slice(i, i + 400)) b.delete(d.ref);
        await b.commit();
      }
      await window._fbDeleteDoc(obRef);
    }
  }

  const leadTlCol = window._fbCollection(database, LEADS, leadId, 'timeline');
  const leadTlSnap = await window._fbGetDocs(leadTlCol);
  for (let i = 0; i < leadTlSnap.docs.length; i += 400) {
    const b = window._fbWriteBatch();
    for (const d of leadTlSnap.docs.slice(i, i + 400)) b.delete(d.ref);
    await b.commit();
  }
  await window._fbDeleteDoc(leadRef);

}

/**
 * Após criar cliente em data/main.
 * @param {string} leadId
 * @param {string} clientId
 */
export async function markLeadConverted(leadId, clientId) {
  const t = nowMs();
  const { uid, name } = actorFromWindow();
  const database = db();
  const ref = window._fbDoc(database, LEADS, leadId);
  await window._fbUpdateDoc(ref, {
    status: LEAD_STATUS.CONVERTIDO,
    convertedAt: t,
    convertedClientId: String(clientId || ''),
    updatedAt: t,
  });
  const evCol = window._fbCollection(database, LEADS, leadId, 'timeline');
  await window._fbAddDoc(evCol, {
    kind: 'convert',
    at: window._fbServerTimestamp(),
    actorUid: uid,
    actorName: name,
    message: `Convertido em cliente (${clientId})`,
    meta: { clientId },
  });
}

export { LEAD_STATUS, LEAD_STATUS_LABEL };
